<template>
  <div class="min-h-screen bg-gray-900 py-8 sm:py-12 px-4">
    <div class="max-w-4xl mx-auto">
      <h1 class="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Demo Management</h1>

      <!-- Create/Edit Demo Form -->
      <div class="bg-gray-800 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg sm:text-xl font-semibold text-white">{{ isEditing ? 'Edit Demo' : 'Create New Demo' }}</h2>
          <button 
            v-if="isEditing"
            @click="cancelEdit"
            class="text-gray-400 hover:text-gray-300 text-sm px-3 py-1 hover:bg-gray-700 rounded-md transition-colors duration-200"
          >
            Cancel Edit
          </button>
        </div>
        <form @submit.prevent="isEditing ? updateDemo() : createDemo()" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Demo Name</label>
            <input 
              v-model="newDemo.name"
              type="text"
              @input="updateDemoId"
              placeholder="e.g., Super Sleeper"
              :class="[
                'w-full px-3 sm:px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 border focus:ring-2 focus:outline-none transition-colors duration-200 text-sm sm:text-base',
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
              ]"
            >
            <p v-if="errors.name" class="mt-1 text-sm text-red-500 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ errors.name }}
            </p>
          </div>
          <div>
            <div class="flex justify-between items-center mb-1">
              <label class="text-sm font-medium text-gray-300">Demo ID</label>
              <span class="text-xs text-gray-500">Auto-generated from name</span>
            </div>
            <div 
              class="w-full px-3 sm:px-4 py-2 bg-gray-700/50 rounded-lg text-gray-400 border border-gray-600/50 font-mono text-sm break-all"
            >
              {{ newDemo.name ? handleize(newDemo.name) : 'demo-id-preview' }}
            </div>
            <p v-if="errors.id" class="mt-1 text-sm text-red-500">{{ errors.id }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Domain</label>
            <input 
              v-model="newDemo.shopify_domain"
              type="text"
              placeholder="e.g., example.com"
              :class="[
                'w-full px-3 sm:px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 border focus:ring-2 focus:outline-none transition-colors duration-200 text-sm sm:text-base',
                errors.shopify_domain ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
              ]"
            >
            <p v-if="errors.shopify_domain" class="mt-1 text-sm text-red-500 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ errors.shopify_domain }}
            </p>
          </div>

          <!-- Questions Section -->
          <div>
            <div class="flex justify-between items-center mb-1">
              <label class="text-sm font-medium text-gray-300">Quick Search Questions</label>
              <span class="text-xs text-gray-500">Optional</span>
            </div>
            <div class="space-y-2">
              <div v-for="(question, index) in newDemo.questions" :key="index" class="flex gap-2">
                <input 
                  v-model="newDemo.questions[index]"
                  type="text"
                  :placeholder="'e.g., What mattress is best for back pain?'"
                  :class="[
                    'flex-1 px-3 sm:px-4 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 border focus:ring-2 focus:outline-none transition-colors duration-200 text-sm sm:text-base',
                    errors.questions && (!question.trim() || question.trim().length < 10) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                  ]"
                >
                <button 
                  @click="removeQuestion(index)"
                  type="button"
                  class="p-2 text-red-400 hover:text-red-300 hover:bg-gray-600/50 rounded-lg transition-colors duration-200"
                  :disabled="newDemo.questions.length === 1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <button 
              @click="addQuestion"
              type="button"
              class="mt-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors duration-200 text-sm flex items-center px-3 py-1.5 rounded-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add another question
            </button>
            <p v-if="errors.questions" class="mt-2 text-sm text-red-500 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ errors.questions }}
            </p>
          </div>

          <button 
            type="submit"
            :disabled="isLoading"
            class="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base mt-6"
          >
            {{ isLoading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Demo' : 'Create Demo') }}
          </button>
        </form>
      </div>

      <!-- Demo List -->
      <div class="bg-gray-800 rounded-xl p-4 sm:p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-white">Existing Demos</h2>
          <div class="flex items-center gap-3">
            <button 
              @click="refreshAllMetrics"
              :disabled="isRefreshing"
              class="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-gray-600/50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :title="isRefreshing ? 'Refreshing metrics...' : 'Refresh all metrics'"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="h-4 w-4" 
                :class="{ 'animate-spin': isRefreshing }"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              <span class="text-sm">Refresh</span>
            </button>
            <div class="text-sm text-gray-400">{{ demos.length }} demo{{ demos.length !== 1 ? 's' : '' }}</div>
          </div>
        </div>
        
        <!-- Grid Layout -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="demo in demos" :key="demo.id" 
               class="bg-gradient-to-br from-gray-700 to-gray-700/50 rounded-xl overflow-hidden border border-gray-600/20 hover:border-gray-500/30 transition-all duration-300">
            <!-- Card Header -->
            <div class="p-4 bg-gray-700/50">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0 mr-4">
                  <h3 class="text-white font-medium text-lg mb-1 truncate">{{ demo.name }}</h3>
                  <div class="flex items-center text-gray-400 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span class="truncate">{{ demo.shopify_domain }}</span>
                  </div>
                </div>
                <!-- Quick Actions -->
                <div class="flex gap-1">
                  <a 
                    :href="'/demos/' + demo.id"
                    target="_blank"
                    class="text-blue-400 hover:text-blue-300 transition-colors duration-200 p-1.5 hover:bg-gray-600/50 rounded-lg"
                    title="View Demo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <!-- Stats Grid -->
            <div class="grid grid-cols-4 divide-x divide-gray-600/20 bg-gray-700/30">
              <div class="px-3 py-3 text-center">
                <div class="text-lg font-semibold text-amber-400">{{ demo.views || 0 }}</div>
                <div class="text-xs text-gray-400 mt-0.5">Views</div>
              </div>
              <div class="px-3 py-3 text-center">
                <div class="text-lg font-semibold text-blue-400">{{ demo.search_count || 0 }}</div>
                <div class="text-xs text-gray-400 mt-0.5">Searches</div>
              </div>
              <div class="px-3 py-3 text-center">
                <div class="text-lg font-semibold text-green-400">{{ demo.product_count || 0 }}</div>
                <div class="text-xs text-gray-400 mt-0.5">Products</div>
              </div>
              <div class="px-3 py-3 text-center">
                <div class="text-lg font-semibold text-purple-400">{{ demo.enriched_count || 0 }}</div>
                <div class="text-xs text-gray-400 mt-0.5">Enriched</div>
              </div>
            </div>

            <!-- Card Actions -->
            <div class="p-4 bg-gray-700/20 flex items-center justify-between">
              <div class="text-xs text-gray-400">
                ID: <span class="font-mono">{{ demo.id }}</span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="startEdit(demo)"
                  class="text-gray-400 hover:text-yellow-300 transition-colors duration-200 p-1.5 hover:bg-gray-600/50 rounded-lg group"
                  title="Edit Demo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  @click="confirmDelete(demo)"
                  :disabled="deletingDemos.includes(demo.id)"
                  class="text-gray-400 hover:text-red-400 transition-colors duration-200 p-1.5 hover:bg-gray-600/50 rounded-lg group disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="deletingDemos.includes(demo.id) ? 'Deleting...' : 'Delete Demo'"
                >
                  <svg v-if="!deletingDemos.includes(demo.id)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="demos.length === 0" class="text-center py-12">
          <div class="text-gray-400 mb-2">No demos created yet</div>
          <div class="text-sm text-gray-500">Create your first demo using the form above</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { db, functions } from '@/config/firebase'
import { collection, getDocs, onSnapshot, query, where, getCountFromServer, doc, updateDoc, deleteDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

const setupDemo = httpsCallable(functions, 'setupDemo')
const editingDemo = ref<any>(null)
const isEditing = ref(false)
const isDeleting = ref(false)
const isRefreshing = ref(false)
const deletingDemos = ref<string[]>([])

const refreshAllMetrics = async () => {
  if (isRefreshing.value) return
  
  isRefreshing.value = true
  try {
    // Update all demos in parallel
    const updatedDemos = await Promise.all(
      demos.value.map(async (demo) => {
        const counts = await getCounts(demo.id)
        return {
          ...demo,
          search_count: counts.searches,
          product_count: counts.products,
          enriched_count: counts.enriched
        }
      })
    )
    demos.value = updatedDemos
  } catch (error) {
    console.error('Error refreshing metrics:', error)
  } finally {
    isRefreshing.value = false
  }
}

const confirmDelete = async (demo: any) => {
  if (!confirm(`Are you sure you want to delete the demo "${demo.name}"? This action cannot be undone.`)) {
    return;
  }

  deletingDemos.value.push(demo.id);
  try {
    // Delete the demo document
    const demoRef = doc(db, 'demos', demo.id);
    await deleteDoc(demoRef);

    // Delete related demo-searches
    const searchesRef = collection(db, 'demo-searches');
    const searchesQuery = query(searchesRef, where('org_id', '==', demo.id));
    const searchesSnapshot = await getDocs(searchesQuery);
    const searchDeletePromises = searchesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(searchDeletePromises);

    // Delete related demo-products
    const productsRef = collection(db, 'demo-products');
    const productsQuery = query(productsRef, where('org_id', '==', demo.id));
    const productsSnapshot = await getDocs(productsQuery);
    const productDeletePromises = productsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(productDeletePromises);

  } catch (error) {
    console.error('Error deleting demo:', error);
    alert('Failed to delete demo. Please try again.');
  } finally {
    deletingDemos.value = deletingDemos.value.filter(id => id !== demo.id);
  }
}

const demos = ref<any[]>([])
const isLoading = ref(false)
const newDemo = ref({
  id: '',
  name: '',
  shopify_domain: '',
  questions: [''] // Initialize with one empty question
})

const errors = ref({
  id: '',
  name: '',
  shopify_domain: '',
  questions: ''
})

const validateDomain = (domain: string) => {
  if (!domain) return 'Domain is required'
  if (domain.endsWith('/')) return 'Domain should not end with /'
  // Basic domain validation - must have at least one dot and no spaces
  if (!domain.includes('.') || domain.includes(' ')) return 'Please enter a valid domain (e.g., example.com)'
  return ''
}

const validateDemoName = (name: string) => {
  if (!name) return 'Demo name is required'
  if (name.length < 3) return 'Demo name must be at least 3 characters long'
  if (name.length > 50) return 'Demo name must be less than 50 characters'
  return ''
}

const validateQuestions = (questions: string[]) => {
  const nonEmptyQuestions = questions.filter(q => q.trim())
  // If there are any non-empty questions, validate their length
  if (nonEmptyQuestions.length > 0 && questions.some(q => q.trim().length > 0 && q.trim().length < 10)) {
    return 'Questions must be at least 10 characters long'
  }
  return ''
}

const handleize = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const updateDemoId = () => {
  if (!isEditing && newDemo.value.name) {
    newDemo.value.id = handleize(newDemo.value.name);
  }
};

const validateFields = () => {
  // Always set the ID from the name before validation
  if (!isEditing && newDemo.value.name) {
    newDemo.value.id = handleize(newDemo.value.name);
  }

      errors.value = {
    id: !newDemo.value.name ? 'Demo name is required to generate ID' : '',
    name: validateDemoName(newDemo.value.name),
    shopify_domain: validateDomain(newDemo.value.shopify_domain),
    questions: validateQuestions(newDemo.value.questions)
  }

  return !Object.values(errors.value).some(error => error);
};

// Get counts for a demo
const getCounts = async (demoId: string) => {
  const searchesRef = collection(db, 'demo-searches')
  const searchQuery = query(searchesRef, where('org_id', '==', demoId))
  const searchSnapshot = await getCountFromServer(searchQuery)

  const productsRef = collection(db, 'demo-products')
  const productsQuery = query(productsRef, where('org_id', '==', demoId))
  const productsSnapshot = await getCountFromServer(productsQuery)

  // Count enriched products (those with embeddings)
  const enrichedQuery = query(
    productsRef, 
    where('org_id', '==', demoId),
    where('embedding', '!=', null)
  )
  const enrichedSnapshot = await getCountFromServer(enrichedQuery)

  return {
    searches: searchSnapshot.data().count,
    products: productsSnapshot.data().count,
    enriched: enrichedSnapshot.data().count
  }
}

// Load demos and listen for updates
onMounted(() => {
  const demosRef = collection(db, 'demos')
  onSnapshot(demosRef, async (snapshot) => {
    // Get demos with their counts
    const demosWithCounts = await Promise.all(
      snapshot.docs.map(async doc => {
        const counts = await getCounts(doc.id)
        return {
          id: doc.id,
          ...doc.data(),
          search_count: counts.searches,
          product_count: counts.products,
          enriched_count: counts.enriched
        }
      })
    )
    demos.value = demosWithCounts
  })
})

const addQuestion = () => {
  newDemo.value.questions.push('')
}

const removeQuestion = (index: number) => {
  if (newDemo.value.questions.length > 1) {
    newDemo.value.questions.splice(index, 1)
  }
}

const startEdit = (demo: any) => {
  editingDemo.value = { ...demo }
  isEditing.value = true
  newDemo.value = {
    id: demo.id,
    name: demo.name,
    shopify_domain: demo.shopify_domain,
    questions: [...(demo.questions || [''])]
  }
}

const cancelEdit = () => {
  isEditing.value = false
  editingDemo.value = null
  newDemo.value = {
    id: '',
    name: '',
    shopify_domain: '',
    questions: ['']
  }
  errors.value = {
    id: '',
    name: '',
    shopify_domain: '',
    questions: ''
  }
}

const updateDemo = async () => {
  if (!validateFields()) return

  isLoading.value = true
  try {
    const demoRef = doc(db, 'demos', newDemo.value.id)
    const questions = newDemo.value.questions.filter(q => q.trim() !== '');
    await updateDoc(demoRef, {
      name: newDemo.value.name,
      shopify_domain: newDemo.value.shopify_domain,
      questions: questions
    })
    
    // Reset form
    cancelEdit()
  } catch (error) {
    console.error('Error updating demo:', error)
    alert('Failed to update demo. Please try again.')
  } finally {
    isLoading.value = false
  }
}

const createDemo = async () => {
  // Ensure ID is set from name before validation
  if (newDemo.value.name) {
    newDemo.value.id = handleize(newDemo.value.name);
  }

  if (!validateFields()) return

  isLoading.value = true
  try {
    const filteredQuestions = newDemo.value.questions.filter(q => q.trim() !== '');
    console.log('Creating demo with ID:', newDemo.value.id); // Debug log
    const result = await setupDemo({
      id: handleize(newDemo.value.name), // Ensure we use fresh handleized version
      name: newDemo.value.name,
      shopify_domain: newDemo.value.shopify_domain.trim(),
      questions: filteredQuestions
    })

    const response = result.data as { success: boolean; message?: string };
    if (!response.success) {
      throw new Error(response.message || 'Failed to create demo');
    }
    
    // Reset form
    newDemo.value = {
      id: '',
      name: '',
      shopify_domain: '',
      questions: ['']
    }
    errors.value = {
      id: '',
      name: '',
      shopify_domain: '',
      questions: ''
    }
  } catch (error) {
    console.error('Error creating demo:', error)
    alert('Failed to create demo. Please try again.')
  } finally {
    isLoading.value = false
  }
}
</script>
